import type { GiftCardDto, OrderMembershipDto, OrderResponse, TicketDto } from 'api/generated/types.gen';
import { getAllOrderTickets, getEventTickets, mapOrderTicketGroups } from 'lib/helpers/order-helpers';

/** How many of a group's items are ticked. Drives the indeterminate checkbox. */
export type GroupSelectionState = 'none' | 'some' | 'all';
export const ticketKey = (ticket: TicketDto) => `ticket:${ticket.encryptedId}`;
export const giftCardKey = (giftCard: GiftCardDto) => `giftcard:${giftCard.encryptedGiftCardId}`;
export const membershipKey = (membership: OrderMembershipDto) => `membership:${membership.encryptedOrderMembershipId}`;

/** Every item on the order that the split endpoint can move, as keys. */
export const getAllItemKeys = (order: OrderResponse | undefined): string[] => order ? [...getAllOrderTickets(order).map(ticketKey), ...(order.giftCards ?? []).map(giftCardKey), ...(order.memberships ?? []).map(membershipKey)] : [];

/**
 * The groups a split leaves behind. The server hands back an order without the
 * ticket groups, subscriptions and events whose tickets have all moved off, so
 * the preview must not keep an event or a subscription card alive on tickets
 * that are no longer under it.
 */
const withoutEmptiedGroups = (order: OrderResponse): OrderResponse => {
  const withGroups: OrderResponse = {
    ...order,
    tickets: (order.tickets ?? []).filter(group => (group.tickets?.length ?? 0) > 0),
    subscriptionTypeGroups: (order.subscriptionTypeGroups ?? []).map(group => ({
      ...group,
      eventTicketGroups: (group.eventTicketGroups ?? []).filter(eventGroup => (eventGroup.tickets?.length ?? 0) > 0)
    })).filter(group => (group.eventTicketGroups ?? []).length > 0)
  };

  // An event is on the order to be shown against its tickets, whether they were
  // bought directly or as part of a subscription.
  return {
    ...withGroups,
    events: (order.events ?? []).filter(event => getEventTickets(withGroups, event.id).length > 0)
  };
};
const separateFees = (ticket: TicketDto) => (ticket.fees ?? []).filter(fee => fee.showAsSeparateItem).reduce((acc, fee) => acc + (fee.price ?? 0), 0);

/**
 * The order as it would stand once everything unticked has been split off, so
 * the cart summary can price a partial selection with the very same rules it
 * prices a whole order by. Exported for testing; a preview only, since the
 * authoritative figures come back on the split response.
 */
export const buildSelectedOrder = (order: OrderResponse | undefined, selected: Set<string>): OrderResponse | undefined => {
  if (!order) return undefined;
  const filtered = withoutEmptiedGroups(mapOrderTicketGroups(order, tickets => tickets.filter(ticket => selected.has(ticketKey(ticket)))));
  const giftCards = (order.giftCards ?? []).filter(giftCard => selected.has(giftCardKey(giftCard)));
  const memberships = (order.memberships ?? []).filter(membership => selected.has(membershipKey(membership)));
  const tickets = getAllOrderTickets(filtered);
  // The subtotal line is ex-tax and ex-separate-fees, matching how the whole
  // order's `price` relates to the breakdown lines beneath it.
  const ticketsExTax = tickets.reduce((acc, ticket) => acc + (ticket.priceWithFees ?? ticket.price ?? 0) - separateFees(ticket), 0);
  const ticketsTax = tickets.reduce((acc, ticket) => acc + (ticket.priceWithFeesAndTax ?? 0) - (ticket.priceWithFees ?? 0), 0);
  const ticketsSeparateFees = tickets.reduce((acc, ticket) => acc + separateFees(ticket), 0);
  // Donations cannot be moved off the order — the split takes encrypted ids and
  // an order donation has none — so they are always paid for here.
  const extrasExTax = giftCards.reduce((acc, giftCard) => acc + (giftCard.price ?? 0), 0) + memberships.reduce((acc, membership) => acc + (membership.price ?? 0), 0) + (order.donations ?? []).reduce((acc, donation) => acc + (donation.price ?? 0), 0);
  // The backend copies the order's fee lines onto both sides of a split, so
  // delivery is owed here whatever the buyer ticks.
  const deliveryPrice = order.deliveryType?.price ?? 0;
  const appliedGiftCards = (order.appliedGiftCards ?? []).reduce((acc, payment) => acc + (payment.amountUsed ?? 0), 0);
  const price = ticketsExTax + extrasExTax + deliveryPrice;
  // What the summary shows above the divider. Delivery is the only charge this
  // preview prices, so it is the whole of the gap between the two, which is the
  // relationship the response itself guarantees. Every figure the summary reads
  // has to be restated here, not just the ones it happens to read today: it
  // takes the preview for an order, so a field left at the full order's value
  // would price the untouched basket above a breakdown of the selected part.
  const subtotal = ticketsExTax + extrasExTax;
  return {
    ...filtered,
    giftCards,
    memberships,
    subtotal,
    subtotalWithTax: subtotal + ticketsTax,
    taxAmount: ticketsTax,
    price,
    priceWithTax: price + ticketsTax,
    // A gift card already applied to the whole order can cover more than the
    // part being paid for. The server caps what is owed at zero rather than
    // handing money back, so the preview must not read as a credit either.
    remainingAmount: Math.max(0, price + ticketsTax + ticketsSeparateFees - appliedGiftCards)
  };
};